import { useEffect, useMemo, useRef, useState } from 'react'

type ProductDB = {
  id: number
  treeType: string
  species: string
  subSpecies?: string
  product: string
  productType: string
  certification?: string
  color?: string
  abbr?: string
}

type Props = {
  label?: string
  productType?: string
  value: string
  onChange: (val: string) => void
  placeholder?: string
}

export default function ProductPicker({ label, productType, value, onChange, placeholder }: Props) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [db, setDb] = useState<ProductDB[] | null>(null)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const res = await fetch('/products-db.json', { cache: 'force-cache' })
        const data: ProductDB[] = await res.json()
        if (!alive) return
        setDb(data)
      } catch (e) {
        console.error('ProductPicker: failed to load products-db.json', e)
        setDb([])
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (!ref.current) return
      if (!ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  const filtered = useMemo(() => {
    if (!db) return []
    const s = q.trim().toLowerCase()
    const type = (productType || '').trim().toLowerCase()
    let rows = db
    if (type) {
      // include if equals OR contains (handles minor naming diffs like "Chip-N-Saw" vs "Chip N Saw")
      rows = rows.filter((r) => {
        const t = (r.productType || '').toLowerCase()
        return t === type || t.includes(type)
      })
    }
    if (!s) return rows.slice(0, 100)
    return rows.filter((r) =>
      [r.product, r.species, r.subSpecies, r.treeType]
        .join(' ')
        .toLowerCase()
        .includes(s),
    ).slice(0, 100)
  }, [db, q, productType])

  function choose(label: string) {
    onChange(label)
    setQ('')
    setOpen(false)
  }

  function makeLabel(r: ProductDB) {
    // Short, readable product label
    // e.g., "Pulpwood — Loblolly Pine" or "Sawtimber — Oak"
    const left = r.product?.trim() || r.productType || 'Product'
    const right = [r.species, r.subSpecies].filter(Boolean).join(' ')
    return right ? `${left} — ${right}` : left
  }

  const showCreate = q.trim().length > 0 && !filtered.some((r) => makeLabel(r).toLowerCase() === q.trim().toLowerCase())

  return (
    <div className="grid gap-2 relative" ref={ref}>
      {label && <label className="text-sm">{label}</label>}
      <div className="flex gap-2">
        <input
          className="input flex-1"
          placeholder={placeholder || 'Search products…'}
          value={open ? q : value}
          onChange={(e) => {
            setQ(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
        />
        <button
          className="btn-secondary"
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          Browse
        </button>
      </div>

      {open && (
        <div className="popover absolute z-40 mt-1 w-full bg-white shadow-xl">
          <div className="px-3 py-2 text-xs text-[var(--muted)] border-b border-[color:var(--border)]">
            {productType ? <>Filtering by <span className="font-medium">{productType}</span></> : 'All product types'}
          </div>
          {!db ? (
            <div className="p-3 text-sm text-[var(--muted)]">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-3 text-sm text-[var(--muted)]">No matches</div>
          ) : (
            <ul className="max-h-72 overflow-auto">
              {filtered.map((r) => {
                const label = makeLabel(r)
                return (
                  <li key={`${r.id}-${label}`}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-[var(--surface-1)]"
                      onClick={() => choose(label)}
                      title={label}
                    >
                      <div className="font-medium">{label}</div>
                      <div className="text-xs text-[var(--muted)]">
                        {r.productType} • {r.treeType}{r.species ? ` • ${r.species}` : ''}
                      </div>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
          {showCreate && (
            <div className="border-t border-[color:var(--border)]">
              <button
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-[var(--surface-1)]"
                onClick={() => choose(q.trim())}
              >
                + Use “{q.trim()}”
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
